import React, { useState, useEffect } from 'react';
import { ModalX } from '../../../../components';
import { useCreateRole, useUpdateRole } from '../../../../custom-hooks/use-roles';
import { Button, Checkbox, Input, Textarea } from '@material-tailwind/react';
import { ProtectedRoutes } from '../../../../router-x/routes';
import { STATIC_USER_ROLES } from '../../../../utils/enums';
import { convertToPresentableText } from '../../../../utils/functions';
import { CornerDownRightIcon } from 'lucide-react';

const INITIAL_FORM_DATA = {
    name: '',
    description: '',
    permissions: ['FX7-1', 'FX7-1:manage'],
};

const migratePermissions = (data) => {
    if (!data || !data.permissions) return data;
    let newPermissions = [...data.permissions];
    const manageToViewEdit = ['FX4', 'FX5', 'FX6', 'FX7', 'FX9', 'FX10'];
    let modified = false;
    for (const route of manageToViewEdit) {
        const manageKey = `${route}:manage`;
        if (newPermissions.includes(manageKey)) {
            newPermissions = newPermissions.filter(p => p !== manageKey);
            if (!newPermissions.includes(`${route}:view`)) newPermissions.push(`${route}:view`);
            if (!newPermissions.includes(`${route}:edit`)) newPermissions.push(`${route}:edit`);
            modified = true;
        }
    }
    return modified ? { ...data, permissions: newPermissions } : data;
};

const RoleFormModal = ({
    open,
    onClose,
    isUpdating,
    selectedRoleId,
    defaultData
}) => {
    const [formData, setFormData] = useState(() => migratePermissions(defaultData || INITIAL_FORM_DATA));

    useEffect(() => {
        if (open) {
            setFormData(migratePermissions(defaultData || INITIAL_FORM_DATA));
        }
    }, [open, defaultData]);

    const addRoleMutation = useCreateRole();
    const updateRoleMutation = useUpdateRole();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isUpdating) {
                await updateRoleMutation.mutateAsync({ roleId: selectedRoleId, data: formData });
            } else {
                await addRoleMutation.mutateAsync(formData);
            }
            onClose();
        } catch (error) {
            console.error("Error saving role:", error);
        }
    };

    const getAllRouteKeys = (route) => {
        let keys = [route.id];
        if (route.availableOperations) {
            route.availableOperations.forEach(op => keys.push(`${route.id}:${op}`));
        }
        if (route.nestedRoutes) {
            route.nestedRoutes.forEach(nested => {
                keys = keys.concat(getAllRouteKeys(nested));
            });
        }
        return keys;
    };

    const handleRouteCheckboxChange = (e, routeId, route) => {
        const { checked } = e.target;
        let newPermissions = [...(formData.permissions || [])];

        if (checked) {
            if (!newPermissions.includes(routeId)) {
                newPermissions.push(routeId);
            }
            // Auto-add single operation
            if (route?.availableOperations?.length === 1) {
                const operationKey = `${routeId}:${route.availableOperations[0]}`;
                if (!newPermissions.includes(operationKey)) {
                    newPermissions.push(operationKey);
                }
            } else if (route?.availableOperations?.length > 1) {
                const hasAnyOp = route.availableOperations.some(op => newPermissions.includes(`${routeId}:${op}`));
                if (!hasAnyOp) {
                    if (route.availableOperations.includes('view')) {
                        newPermissions.push(`${routeId}:view`);
                    } else if (route.availableOperations.includes('R')) {
                        newPermissions.push(`${routeId}:R`);
                    } else {
                        newPermissions.push(`${routeId}:${route.availableOperations[0]}`);
                    }
                }
            }
        } else {
            const keysToRemove = getAllRouteKeys(route);
            newPermissions = newPermissions.filter(p => !keysToRemove.includes(p));
        }

        setFormData(prev => ({ ...prev, permissions: newPermissions }));
    };

    const handleOperationCheckboxChange = (e, routeId, operation, route) => {
        const { checked } = e.target;
        const operationKey = `${routeId}:${operation}`;
        let newPermissions = [...(formData.permissions || [])];

        if (checked) {
            if (!newPermissions.includes(operationKey)) {
                newPermissions.push(operationKey);
            }
            // Always ensure the parent route ID is included
            if (!newPermissions.includes(routeId)) {
                newPermissions.push(routeId);
            }
        } else {
            newPermissions = newPermissions.filter(p => p !== operationKey);
            
            if (operation === 'view' || operation === 'R') {
                const keysToRemove = getAllRouteKeys(route);
                newPermissions = newPermissions.filter(p => !keysToRemove.includes(p));
            } else {
                // If no operations remain, remove the route ID itself
                const anyOpRemaining = route?.availableOperations?.some(op =>
                    newPermissions.includes(`${routeId}:${op}`)
                );
                if (!anyOpRemaining) {
                    const keysToRemove = getAllRouteKeys(route);
                    newPermissions = newPermissions.filter(p => !keysToRemove.includes(p));
                }
            }
        }

        // Mutually exclusive logic for leads
        if (checked && routeId === 'FX11') {
            if (operation === 'manage' && newPermissions.includes(`FX11:add_only`)) {
                newPermissions = newPermissions.filter(p => p !== `FX11:add_only`);
            }
            if (operation === 'add_only' && newPermissions.includes(`FX11:manage`)) {
                newPermissions = newPermissions.filter(p => p !== `FX11:manage`);
            }
        }

        setFormData(prev => ({ ...prev, permissions: newPermissions }));
    };

    const isRouteSelected = (routeId) => {
        return (formData.permissions || []).includes(routeId);
    };

    const hasOperationPermission = (routeId, operation) => {
        if (!isRouteSelected(routeId)) return false;
        return (formData.permissions || []).includes(`${routeId}:${operation}`);
    };

    // Only show admin-side routes (filter out customer, team member, receptionist, support agent specific pages)
    const adminRoutes = ProtectedRoutes.filter(route =>
        route.inSideBarList &&
        !route.permissibleRoles?.includes(STATIC_USER_ROLES.USER) &&
        !route.permissibleRoles?.includes(STATIC_USER_ROLES.TEAM_MEMBER) &&
        !route.permissibleRoles?.includes(STATIC_USER_ROLES.RECEPTIONIST) &&
        !(route.permissibleRoles?.length === 1 && route.permissibleRoles[0] === STATIC_USER_ROLES.SUPPORT_AGENT)
    );

    const groupedRoutes = adminRoutes.reduce((acc, route) => {
        const groupName = route.group || 'General';
        if (!acc[groupName]) {
            acc[groupName] = [];
        }
        acc[groupName].push(route);
        return acc;
    }, {});

    return (
        <ModalX
            isOpen={open}
            onClose={onClose}
            title={isUpdating ? 'Update Role' : 'Create New Role'}
            onSubmit={handleSubmit}
            maxWidth='xxxl'
        >
            <form onSubmit={handleSubmit}>
                <div className='flex flex-col md:flex-row gap-4'>
                    <div className='p-4 space-y-4 w-full md:w-1/3'>
                        <Input
                            label='Role Name'
                            name='name'
                            value={formData.name || ''}
                            onChange={handleChange}
                            required
                        />
                        <Textarea
                            label='Description'
                            name='description'
                            value={formData.description || ''}
                            onChange={handleChange}
                            rows={4}
                        />
                        {formData.isSystemRole && (
                            <div className='text-xs text-orange-800 bg-orange-50 p-2 rounded-lg'>
                                ⚠ This is a system role. It cannot be deleted, but its permissions can be modified.
                            </div>
                        )}
                    </div>
                    <div className='w-full md:w-2/3 p-4'>
                        <h1 className='text-xl font-bold mb-4'>Allowed Pages & Permissions</h1>
                        <div className='overflow-y-auto max-h-[60vh]'>
                            <div className='flex gap-2 mb-2 font-semibold px-2'>
                                <div className='w-1/3'>Page</div>
                                <div className='w-2/3'>
                                    <div>Permissions</div>
                                </div>
                            </div>
                            <div className='space-y-3 border rounded-lg p-2'>
                                {Object.entries(groupedRoutes).map(([groupName, routesInGroup]) => (
                                    <div key={groupName} className='mb-4 last:mb-0'>
                                        <h3 className='font-bold text-gray-700 bg-gray-100 p-2 rounded-md mb-2'>{groupName}</h3>
                                        <div className='space-y-3 ml-2'>
                                            {routesInGroup.map((route, i) => (
                                                <div key={i} className='border-b pb-3 last:border-b-0'>
                                                    {/* Parent Route */}
                                        <div className='flex items-start gap-2 mb-2'>
                                            <div className='w-1/3 pr-2'>
                                                <Checkbox
                                                    label={route.name}
                                                    checked={isRouteSelected(route.id)}
                                                    onChange={(e) => handleRouteCheckboxChange(e, route.id, route)}
                                                    className="hover:before:opacity-0"
                                                />
                                            </div>
                                            <div className='w-2/3 flex flex-wrap gap-x-4 gap-y-2 items-center'>
                                                {route.availableOperations.map((operation, j) => (
                                                    <Checkbox
                                                        key={`${route.id}-op-${j}`}
                                                        label={UserPermissionsRender(operation)}
                                                        checked={hasOperationPermission(route.id, operation)}
                                                        onChange={(e) => handleOperationCheckboxChange(e, route.id, operation, route)}
                                                        disabled={!isRouteSelected(route.id)}
                                                        className="hover:before:opacity-0"
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Nested Routes */}
                                        {route?.nestedRoutes?.filter(nr => !nr.hideInRoleForm)?.length > 0 && (
                                            <div className='ml-6 pl-2 border-l-2 border-gray-200'>
                                                {route.nestedRoutes.filter(nr => !nr.hideInRoleForm).map((nestedRoute, k) => (
                                                    <div key={`nested-${k}`} className='mt-2'>
                                                        <div className='flex items-start gap-2'>
                                                            <div className='w-1/3 flex items-start pr-2'>
                                                                <CornerDownRightIcon className='w-4 h-4 text-gray-500 mr-1 mt-1 shrink-0' />
                                                                <Checkbox
                                                                    label={nestedRoute.name}
                                                                    checked={isRouteSelected(nestedRoute.id)}
                                                                    onChange={(e) => handleRouteCheckboxChange(e, nestedRoute.id, nestedRoute)}
                                                                    disabled={!isRouteSelected(route.id)}
                                                                    className="hover:before:opacity-0"
                                                                />
                                                            </div>
                                                            <div className='w-2/3 flex flex-wrap gap-x-4 gap-y-2 items-center'>
                                                                {nestedRoute.availableOperations.map((operation, l) => (
                                                                    <Checkbox
                                                                        key={`${nestedRoute.id}-op-${l}`}
                                                                        label={UserPermissionsRender(operation)}
                                                                        checked={hasOperationPermission(nestedRoute.id, operation)}
                                                                        onChange={(e) => handleOperationCheckboxChange(e, nestedRoute.id, operation, nestedRoute)}
                                                                        disabled={!isRouteSelected(nestedRoute.id) || !isRouteSelected(route.id)}
                                                                        className="hover:before:opacity-0"
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {/* Grandchildren Routes */}
                                                        {nestedRoute?.nestedRoutes?.filter(gr => !gr.hideInRoleForm)?.length > 0 && (
                                                            <div className='ml-6 pl-2 border-l-2 border-gray-200 mt-2'>
                                                                {nestedRoute.nestedRoutes.filter(gr => !gr.hideInRoleForm).map((grandchildRoute, m) => (
                                                                    <div key={`grandchild-${m}`} className='flex items-start gap-2 mt-2'>
                                                                        <div className='w-1/3 flex items-start pr-2'>
                                                                            <CornerDownRightIcon className='w-4 h-4 text-gray-400 mr-1 mt-1 shrink-0' />
                                                                            <Checkbox
                                                                                label={grandchildRoute.name}
                                                                                checked={isRouteSelected(grandchildRoute.id)}
                                                                                onChange={(e) => handleRouteCheckboxChange(e, grandchildRoute.id, grandchildRoute)}
                                                                                disabled={!isRouteSelected(nestedRoute.id)}
                                                                                className="hover:before:opacity-0"
                                                                            />
                                                                        </div>
                                                                        <div className='w-2/3 flex flex-wrap gap-x-4 gap-y-2 items-center'>
                                                                            {grandchildRoute.availableOperations.map((operation, n) => (
                                                                                <Checkbox
                                                                                    key={`${grandchildRoute.id}-op-${n}`}
                                                                                    label={UserPermissionsRender(operation)}
                                                                                    checked={hasOperationPermission(grandchildRoute.id, operation)}
                                                                                    onChange={(e) => handleOperationCheckboxChange(e, grandchildRoute.id, operation, grandchildRoute)}
                                                                                    disabled={!isRouteSelected(grandchildRoute.id) || !isRouteSelected(nestedRoute.id)}
                                                                                    className="hover:before:opacity-0"
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex justify-end gap-2 mt-4 p-4'>
                    <Button
                        variant="outlined"
                        color="gray"
                        className='rounded-full'
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        className='rounded-full'
                        loading={isUpdating ? updateRoleMutation.isLoading : addRoleMutation.isLoading}
                    >
                        {isUpdating ? 'Update' : 'Save'}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default RoleFormModal;

export const UserPermissionsRender = (permission) => {
    switch (permission) {
        case 'manage':
            return 'Full Access';
        case 'management':
            return 'Management';
        case 'booking':
            return 'Booking';
        case 'view':
            return 'View';
        case 'edit':
            return 'Edit';
        case 'add_only':
            return 'Add Only';
        case 'overview_cards':
            return 'Seating & Revenue';
        case 'pie_charts':
            return 'Charts';
        case 'history_metrics':
            return 'History & Metrics';
        case 'branch_summary':
            return 'Branch-Wise Summary';
        case 'R':
            return 'Access (Read/View)';
        case 'U':
            return 'Update';
        case 'C':
            return 'Create';
        case 'D':
            return 'Delete';
        case 'create_contract':
            return 'Create Contract';
        case 'read_contract':
            return 'Read Contract';
        default:
            return convertToPresentableText(permission);
    }
};
